import React, { useEffect, useMemo, useState } from 'react';
import { Download, Share2, Smartphone, WifiOff, X } from 'lucide-react';

const IOS_INSTALL_REMINDER_KEY = 'pwa-install-ios-remind-after-v2';
const DEFAULT_INSTALL_REMINDER_KEY = 'pwa-install-remind-after';

const isIosDevice = () => (
  /iphone|ipad|ipod/i.test(window.navigator.userAgent)
  || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
);

const isSafariBrowser = () => (
  /safari/i.test(window.navigator.userAgent)
  && !/crios|fxios|edgios|opios|duckduckgo|gsa|instagram|fban|fbav|whatsapp/i.test(window.navigator.userAgent)
);

const isStandalone = () => (
  window.matchMedia?.('(display-mode: standalone)').matches
  || window.navigator.standalone === true
);

export default function PwaExperience() {
  const [installEvent, setInstallEvent] = useState(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  const ios = useMemo(isIosDevice, []);
  const safari = useMemo(isSafariBrowser, []);
  const reminderKey = ios ? IOS_INSTALL_REMINDER_KEY : DEFAULT_INSTALL_REMINDER_KEY;
  const [dismissed, setDismissed] = useState(() => Number(localStorage.getItem(isIosDevice() ? IOS_INSTALL_REMINDER_KEY : DEFAULT_INSTALL_REMINDER_KEY) || 0) > Date.now());
  const [installing, setInstalling] = useState(false);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  useEffect(() => {
    const onInstallPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
    };
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onInstalled = () => {
      setInstallEvent(null);
      setDismissed(true);
    };
    window.addEventListener('beforeinstallprompt', onInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('beforeinstallprompt', onInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const dismiss = (days = 7) => {
    localStorage.setItem(reminderKey, String(Date.now() + days * 24 * 60 * 60 * 1000));
    setDismissed(true);
  };

  const install = async () => {
    if (!installEvent || installing) return;
    setInstalling(true);
    try {
      await installEvent.prompt();
      await installEvent.userChoice;
      setInstallEvent(null);
    } finally {
      setInstalling(false);
    }
  };

  if (!online) {
    return (
      <div className="fixed inset-x-3 bottom-20 z-[100] mx-auto flex max-w-xl items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-xl lg:bottom-3" role="status">
        <WifiOff className="h-5 w-5 shrink-0" />
        <div className="min-w-0 flex-1"><b>Sin internet.</b> Las funciones preparadas para trabajo sin conexión conservarán la captura; las demás te pedirán reconectar antes de guardar.</div>
      </div>
    );
  }

  if (dismissed || isStandalone() || (!installEvent && !ios)) return null;

  return <>
    <div className="fixed inset-x-3 bottom-20 z-[100] mx-auto flex max-w-xl items-center gap-3 rounded-2xl border border-[#d9b8be] bg-white px-4 py-3 text-sm text-slate-800 shadow-2xl lg:bottom-3" role="dialog" aria-label="Instalar AR Gestión">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#8A1C2C] text-white"><Download className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <b className="block text-slate-950">Instalar AR Gestión</b>
        <span className="text-xs text-slate-500">{ios ? 'Guárdala en la pantalla de inicio de tu iPhone.' : 'Ábrela como aplicación, sin pestañas ni barra del navegador.'}</span>
      </div>
      {installEvent ? <button type="button" onClick={install} disabled={installing} className="rounded-xl bg-[#8A1C2C] px-3 py-2 font-semibold text-white disabled:opacity-60">{installing ? 'Instalando…' : 'Instalar'}</button> : ios ? <button type="button" onClick={() => setIosHelpOpen(true)} className="rounded-xl bg-[#8A1C2C] px-3 py-2 font-semibold text-white">Instalar</button> : null}
      <button type="button" onClick={() => dismiss(7)} className="hidden rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 sm:block">Después</button>
      <button type="button" onClick={() => dismiss(30)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" aria-label="Cerrar por 30 días"><X className="h-4 w-4" /></button>
    </div>

    {iosHelpOpen ? <div className="fixed inset-0 z-[130] flex items-end justify-center bg-slate-950/55 p-3 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="ios-install-title" onClick={() => setIosHelpOpen(false)}>
      <div className="w-full max-w-md rounded-[26px] bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#8A1C2C] text-white"><Smartphone className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1"><p className="text-xs font-bold uppercase tracking-[.16em] text-[#9D2235]">En iPhone o iPad</p><h2 id="ios-install-title" className="mt-1 text-xl font-semibold text-slate-950">Agregar AR Gestión a inicio</h2></div>
          <button type="button" onClick={() => setIosHelpOpen(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label="Cerrar"><X className="h-5 w-5" /></button>
        </div>

        {!safari ? <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950"><b>Primero ábrela en Safari.</b><br />Toca el menú del navegador o de WhatsApp y elige <b>Abrir en Safari</b>. Después sigue estos pasos.</div> : null}

        <ol className="mt-5 space-y-3 text-sm text-slate-700">
          <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-bold text-white">1</span><span className="pt-1">En Safari, toca <b>Compartir</b> <Share2 className="ml-1 inline h-4 w-4 text-[#8A1C2C]" />.</span></li>
          <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-bold text-white">2</span><span className="pt-1">Desliza y selecciona <b>Agregar a inicio</b>.</span></li>
          <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-bold text-white">3</span><span className="pt-1">Toca <b>Agregar</b>. El icono aparecerá junto a tus aplicaciones.</span></li>
        </ol>

        <button type="button" onClick={() => { setIosHelpOpen(false); dismiss(7); }} className="mt-5 h-12 w-full rounded-xl bg-[#8A1C2C] px-4 text-sm font-semibold text-white">Entendido</button>
      </div>
    </div> : null}
  </>;
}
